import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';

import { apiUrlGenerator } from '../use-api';
import {
  engagementAttemptsColors,
  engagementAttemptsLabels,
  engagementAttemptsValues,
  engagementAttemptsVisibleValues,
  statusBreakdownColors,
  statusBreakdownLabels,
  statusBreakdownStatuses,
} from './constants';

import type {
  IBreakdownDatum,
  TEngagementAttemptsValue,
  TPatientEngagementReportFilter,
} from './types';

const statusCountUrl =
  'reports/reports/patient-engagement-status-report/status-count';
const engagementAttemptsCountUrl =
  'reports/reports/patient-engagement-status-report/engagement-attempts-count';

interface IStatusCountDatum {
  category: string;
  count: number;
}

interface IStatusCountResponse {
  items: IStatusCountDatum[];
}

interface IEngagementAttemptsCountDatum {
  category: TEngagementAttemptsValue;
  count: number;
}

interface IEngagementAttemptsCountResponse {
  items: IEngagementAttemptsCountDatum[];
}

/**
 * The two donut charts. Filtered by Clinic/Provider/Child Clinic/Status/
 * Engagement Attempts only - Patient Filter and Month do not affect these.
 *
 * The Status Overview donut is sourced from the `status-count` endpoint, the
 * Engagement Attempts Overview from `engagement-attempts-count` (one slice per
 * Monday engagement option; zero-count slices are filtered out).
 */
export const useGetPatientEngagementBreakdown = (
  filter: TPatientEngagementReportFilter,
) => {
  const queryFilter = {
    clinicGuid: filter.clinicGuid,
    parentClinicGuid: filter.parentClinicGuid,
    doctorGuid: filter.doctorGuid,
    status: filter.status,
    engagementAttempts: filter.engagementAttempts,
  };

  const {
    data: statusData,
    isLoading: isStatusLoading,
    isFetching: isStatusFetching,
  } = useQuery<IStatusCountResponse | undefined, string | undefined>({
    queryKey: ['getPatientEngagementStatusCount', queryFilter],
    queryFn: async () => {
      const { data: response, error } =
        await dataService.getList<IStatusCountResponse>(
          apiUrlGenerator(statusCountUrl, queryFilter),
        );
      if (error) return Promise.reject(error);
      return response;
    },
    keepPreviousData: true,
  });

  const {
    data: engagementAttemptsData,
    isLoading: isEngagementAttemptsLoading,
    isFetching: isEngagementAttemptsFetching,
  } = useQuery<
    IEngagementAttemptsCountResponse | undefined,
    string | undefined
  >({
    queryKey: ['getPatientEngagementAttemptsCount', queryFilter],
    queryFn: async () => {
      const { data: response, error } =
        await dataService.getList<IEngagementAttemptsCountResponse>(
          apiUrlGenerator(engagementAttemptsCountUrl, queryFilter),
        );
      if (error) return Promise.reject(error);
      return response;
    },
    keepPreviousData: true,
  });

  const statusBreakdown: IBreakdownDatum[] = useMemo(() => {
    const countByCategory = new Map(
      (statusData?.items ?? []).map(({ category, count }) => [category, count]),
    );
    return statusBreakdownStatuses
      .map((status) => ({
        name: statusBreakdownLabels[status],
        value: countByCategory.get(status) ?? 0,
        color: statusBreakdownColors[status],
      }))
      .filter((entry) => entry.value > 0);
  }, [statusData]);

  const engagementAttemptsBreakdown: IBreakdownDatum[] = useMemo(() => {
    const countByCategory = new Map(
      (engagementAttemptsData?.items ?? []).map(({ category, count }) => [
        category,
        count,
      ]),
    );
    return engagementAttemptsValues
      .filter((value) => engagementAttemptsVisibleValues.has(value))
      .map((value) => ({
        name: engagementAttemptsLabels[value],
        value: countByCategory.get(value) ?? 0,
        color: engagementAttemptsColors[value],
      }))
      .filter((entry) => entry.value > 0);
  }, [engagementAttemptsData]);

  return {
    engagementAttemptsBreakdown,
    statusBreakdown,
    isLoading:
      isStatusLoading ||
      isStatusFetching ||
      isEngagementAttemptsLoading ||
      isEngagementAttemptsFetching,
  };
};
